import { CalendarEvent } from '@demind-inc/core';
import { ReactNode, createContext, useContext, useState } from 'react';
import dayjs from 'dayjs';
import { debounce, isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { CalendarEventCreateOption } from '../types';
import { useCalendarContext } from './CalendarProvider';
import { useEventActions } from '../hooks/useEventActions';

interface ICreateEventMenuContext {
  creatingEvent?: Partial<CalendarEvent>;
  handleSelectNewEvent: (props: CreateEventProps) => void;
  handleCreatingEventChange: (event: Partial<CalendarEvent>) => void;
  handleCreateEvent: (newEvent: CalendarEventCreateOption) => void;
  clearCreatingEvent: () => void;
}

export interface CreateEventProps {
  start: string;
  end: string;
  title?: string;
  description?: string;
  taskId?: string;
}

export const CreateEventMenuContext = createContext({} as ICreateEventMenuContext);
export const useCreateEventMenuContext = () => useContext(CreateEventMenuContext);

export const CreateEventMenuProvider = ({ children }: { children: ReactNode }) => {
  const [creatingEvent, setCreatingEvent] = useState<Partial<CalendarEvent> | undefined>();
  const { createCalendarEvent } = useEventActions();
  const { mainCalendar } = useCalendarContext();

  const handleCreatingEventChange = debounce((event: Partial<CalendarEvent>) => {
    setCreatingEvent((prev) => ({
      ...prev,
      ...event,
    }));
  }, 500);

  const handleSelectNewEvent = ({ start, end, title, taskId, description }: CreateEventProps) => {
    setCreatingEvent({
      eventId: 'creating',
      summary: title || '',
      description: description || '',
      start: { date: start, timeZone: dayjs.tz.guess() },
      end: {
        date: end,
        timeZone: dayjs.tz.guess(),
      },
      recommendedTaskId: taskId,
    });
  };

  const handleCreateEvent = (newEvent: CalendarEventCreateOption) => {
    if (isEmpty(newEvent)) {
      return;
    }

    const newEventId = uuidv4().replace(/-/g, '');
    const color = mainCalendar?.color;
    const newEventOption = {
      ...newEvent,
      color,
      eventId: newEventId,
    };
    createCalendarEvent({
      calendarId: newEvent?.calendarId,
      newEventOption,
    });

    clearCreatingEvent();
  };

  const clearCreatingEvent = () => {
    setCreatingEvent(undefined);
  };

  return (
    <CreateEventMenuContext.Provider
      value={{
        creatingEvent,
        handleSelectNewEvent,
        handleCreateEvent,
        clearCreatingEvent,
        handleCreatingEventChange,
      }}
    >
      {children}
    </CreateEventMenuContext.Provider>
  );
};
